<script lang="ts" setup>
import type { Article, Team } from '~/types'
import type { PageType } from '~/enums/page-type'

interface Props {
  articleId: number
  categories?: Article.Category[]
  team?: Team.Model
  type?: PageType
}

const props = withDefaults(defineProps<Props>(), {
  categories: () => [],
})

const articles = ref<Article.Model[]>([])
const isAdditionalArticlesFetched = ref(false)
const perPage = 3

const hasArticles = computed<boolean>(() => articles.value.length > 0)

const categoryIds = computed<number[]>(() => {
  return props.categories.map((category: Article.Category) => category.id)
})

const teamId = computed<number | undefined>(() => props.team?.id)

const params = computed<Article.SearchRequest>(() => ({
  include: ['image'],
  sort: ['-published_at'],
  per_page: perPage,
  filter: {
    'categories.id': categoryIds.value,
    'id_exclude': [props.articleId],
    'teams.id': teamId.value,
    'type': props.type,
  },
}))

const { fetchData } = useLoadRelatedArticles(params)

const { data } = await useAsyncData(
  `related-articles-${props.articleId}`,
  () => fetchData(),
)

const isAdditionalItemsNeeded = computed<boolean>(() => {
  return !isAdditionalArticlesFetched.value && articles.value.length < perPage
})

const additionalParams = computed<Article.SearchRequest>(() => ({
  ...params.value,
  per_page: perPage - articles.value.length,
  filter: {
    'id_exclude': [props.articleId, ...articles.value.map((article: Article.Model) => article.id)],
    'teams.id': teamId.value,
    'type': props.type,
  },
}))

const fetchAdditionalArticles = async (): Promise<void> => {
  if (!isAdditionalItemsNeeded.value) {
    return
  }

  const { fetchData } = useLoadRelatedArticles(additionalParams)

  const { data } = await useAsyncData(
    `related-articles-${props.articleId}-additional`,
    () => fetchData(),
  )

  isAdditionalArticlesFetched.value = true
  articles.value.push(...(data.value || []))
}

articles.value.push(...(data.value || []))
await nextTick()
await fetchAdditionalArticles()
</script>

<template>
  <section
    v-if="hasArticles"
    class="related-articles"
  >
    <Wrapper>
      <div class="related-articles__heading">
        <UIDynamicHeading
          level="3"
          as="h3"
          class="related-articles__title"
        >
          Related Articles
        </UIDynamicHeading>
      </div>
      <div class="related-articles__list">
        <ArticleRelatedItem
          v-for="article in articles"
          :key="article.id"
          :article
          class="related-articles__item"
        />
      </div>
    </Wrapper>
  </section>
</template>

<style scoped lang="scss">
.related-articles {
  @apply bg-primary py-[40px];
  @apply lg:py-[60px];

  &__heading {
    @apply mb-[30px];
    @apply lg:mb-[50px];
  }

  &__title {
    @apply text-white;
  }

  &__list {
    @apply flex flex-wrap justify-center -mx-[10px];
  }

  &__item {
    @apply w-full px-[10px] mb-[30px] last:mb-0;
    @apply md:w-1/2;
    @apply lg:w-1/3 lg:mb-0;
  }
}
</style>
